import {
  cloneDeep,
  isAllEmpty,
  isIncludeAllChildren,
  isString
} from "@pureadmin/utils";
import { useTimeoutFn } from "@vueuse/core";
import { isProxy, toRaw } from "vue";
import { createWebHashHistory, createWebHistory } from "vue-router";

import { usePermissionStoreHook } from "@/store/modules/permission";

import { router } from "./index";

/** 通过指定 `key` 获取父级路径集合，默认 `key` 为 `path` */
function getParentPaths(value, routes, key = "path") {
  // 深度遍历查找
  function dfs(routes, value, parents) {
    for (let i = 0; i < routes.length; i++) {
      const item = routes[i];
      // 返回父级path
      if (item[key] === value) return parents;
      // children不存在或为空则不递归
      if (!item.children || !item.children.length) continue;
      // 往下查找时将当前path入栈
      parents.push(item.path);

      if (dfs(item.children, value, parents).length) return parents;
      // 深度遍历查找未找到时当前path 出栈
      parents.pop();
    }
    // 未找到时返回空数组
    return [];
  }

  return dfs(routes, value, []);
}

/** 查找对应 `path` 的路由信息 */
function findRouteByPath(path, routes) {
  let res = routes.find(item => item.path == path);
  if (res) {
    return isProxy(res) ? toRaw(res) : res;
  } else {
    for (let i = 0; i < routes.length; i++) {
      if (
        routes[i].children instanceof Array &&
        routes[i].children.length > 0
      ) {
        res = findRouteByPath(path, routes[i].children);
        if (res) {
          return isProxy(res) ? toRaw(res) : res;
        }
      }
    }
    return null;
  }
}

function addPathMatch() {
  if (!router.hasRoute("pathMatch")) {
    router.addRoute({
      path: "/:pathMatch(.*)",
      name: "pathMatch",
      redirect: "/error/404"
    });
  }
}

/** 处理缓存路由（添加、删除、刷新） */
function handleAliveRoute({ name }, mode) {
  switch (mode) {
    case "add":
      usePermissionStoreHook().cacheOperate({
        mode: "add",
        name
      });
      break;
    case "delete":
      usePermissionStoreHook().cacheOperate({
        mode: "delete",
        name
      });
      break;
    case "refresh":
      usePermissionStoreHook().cacheOperate({
        mode: "refresh",
        name
      });
      break;
    default:
      usePermissionStoreHook().cacheOperate({
        mode: "delete",
        name
      });
      useTimeoutFn(() => {
        usePermissionStoreHook().cacheOperate({
          mode: "add",
          name
        });
      }, 100);
  }
}

/** 获取路由历史模式 https://next.router.vuejs.org/zh/guide/essentials/history-mode.html */
function getHistoryMode(routerHistory) {
  // len为1 代表只有历史模式 为2 代表历史模式中存在base参数 https://next.router.vuejs.org/zh/api/#%E5%8F%82%E6%95%B0-1
  const historyMode = routerHistory.split(",");
  const leftMode = historyMode[0];
  const rightMode = historyMode[1];
  // no param
  if (historyMode.length === 1) {
    if (leftMode === "hash") {
      return createWebHashHistory("");
    } else if (leftMode === "h5") {
      return createWebHistory("");
    }
  } //has param
  else if (historyMode.length === 2) {
    if (leftMode === "hash") {
      return createWebHashHistory(rightMode);
    } else if (leftMode === "h5") {
      return createWebHistory(rightMode);
    }
  }
}

/** 获取当前页面按钮级别的权限 */
function getAuths() {
  return router.currentRoute.value.meta.auths;
}

/** 是否有按钮级别的权限 */
function hasAuth(value) {
  if (!value) return false;
  /** 从当前路由的`meta`字段里获取按钮级别的所有自定义`code`值 */
  const metaAuths = getAuths();
  if (!metaAuths) return false;
  const isAuths = isString(value)
    ? metaAuths.includes(value)
    : isIncludeAllChildren(value, metaAuths);
  return isAuths ? true : false;
}
/**
 * 增加路由
 * @param {*} modules 模块的自定义路由
 */
const addRoutes = modules => {
  let routes = [];

  Object.keys(modules).forEach(key => {
    routes = [].concat(routes, modules[key].default);
  });

  routes.forEach(item => {
    const hasRouter = router.options.routes.find(it => it.path === item.path);
    if (hasRouter) {
      router.removeRoute(hasRouter.name);
    }
    router.addRoute(item);
  });
  usePermissionStoreHook().handleWholeMenus(routes);
};

/** 过滤meta中showLink为false的菜单 */
function filterTree(data) {
  const newTree = cloneDeep(data).filter(v => v.meta?.showLink !== false);
  newTree.forEach(v => v.children && (v.children = filterTree(v.children)));
  return newTree;
}
/** 过滤children长度为0的的目录，当目录下没有菜单时，会过滤此目录，目录没有赋予roles权限，当目录下只要有一个菜单有显示权限，那么此目录就会显示 */
function filterChildrenTree(data) {
  const newTree = cloneDeep(data).filter(v => v?.children?.length !== 0);
  newTree.forEach(v => v.children && (v.children = filterTree(v.children)));
  return newTree;
}
/** 从sessionStorage里取出当前登陆用户的角色roles，过滤无权限的菜单 */
function filterNoPermissionTree(data) {
  const newTree = cloneDeep(data);
  return filterChildrenTree(newTree);
}

function handRank(routeInfo) {
  const { name, path, parentId, meta } = routeInfo;
  return isAllEmpty(parentId)
    ? isAllEmpty(meta?.rank) ||
      (meta?.rank === 0 && name !== "Home" && path !== "/")
      ? true
      : false
    : false;
}

/** 按照路由中meta下的rank等级升序来排序路由 */
function ascending(arr) {
  arr.forEach((v, index) => {
    // 当rank不存在时，根据顺序自动创建，首页路由永远在第一位
    if (handRank(v)) v.meta.rank = index + 2;
  });
  return arr.sort((a, b) => {
    return a?.meta.rank - b?.meta.rank;
  });
}

export {
  hasAuth,
  getAuths,
  addPathMatch,
  getHistoryMode,
  getParentPaths,
  findRouteByPath,
  handleAliveRoute,
  addRoutes,
  filterNoPermissionTree,
  ascending,
  filterTree
};
